import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "automation-wizard-condition" }
const _hoisted_2 = {
  key: 0,
  class: "automation-wizard-condition__dashed-border"
}
const _hoisted_3 = { class: "automation-wizard-condition__skeleton" }
const _hoisted_4 = { class: "automation-wizard-condition__skeleton-header" }
const _hoisted_5 = { class: "automation-wizard-condition__skeleton-row" }
const _hoisted_6 = {
  key: 0,
  class: "automation-wizard-condition__and-separator typographies-paragraph-caps-small-semibold"
}
const _hoisted_7 = {
  key: 2,
  class: "automation-wizard-condition__dashed-border"
}
const _hoisted_8 = { class: "automation-wizard-condition__empty" }
const _hoisted_9 = { class: "automation-wizard-condition__empty-text" }
const _hoisted_10 = { class: "automation-wizard-condition__empty-title typographies-paragraph-medium-semibold" }
const _hoisted_11 = { class: "automation-wizard-condition__empty-subtitle typographies-paragraph-small" }

import { ref, computed, onMounted, inject } from 'vue';
import { Translator } from 'types/injects/translator.type';
import { PushToast } from 'types/injects/pushToast.type';
import { getAllCustomFields } from 'api/customfields.js';
import {
    SUPPORTED_CF_TYPES,
    ConditionScope,
    PROPERTY_GROUP_TO_CF_OBJECT,
} from 'Dictionaries/automation/conditionOperators';
import AutomationWizardConditionRow from 'vcomponents/automation/templates/Wizard/Condition/AutomationWizardConditionRow.vue';
import type {
    Condition,
    ConditionField,
    CustomField,
    FieldGroup,
    GroupedCustomFields,
    PropertyDefinition,
} from 'vcomponents/automation/templates/Wizard/Condition/types';

interface Props {
    objectTypes: string[];
    propertyDefinitions?: PropertyDefinition[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AutomationWizardCondition',
  props: /*@__PURE__*/_mergeModels({
    objectTypes: {},
    propertyDefinitions: { default: () => [] }
  }, {
    "modelValue": { default: () => [] },
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose }) {

const OBJECT_TYPE_LABELS: Record<string, string> = {
    document: 'common.labels.document',
    opportunity: 'common.labels.opportunity',
    contact: 'common.labels.contact',
    client: 'common.labels.company',
    prospect: 'common.labels.prospect',
    supplier: 'common.labels.supplier',
    item: 'common.labels.item',
    service: 'common.labels.service',
    ticket: 'common.labels.ticket',
    task: 'common.labels.task',
    purchase: 'common.labels.purchase',
    project: 'common.labels.project',
    staff: 'common.labels.staff',
    site: 'common.labels.site',
};

const props = __props;

const conditions = _useModel<Condition[]>(__props, "modelValue");

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const translations: Record<string, string> = {
    addCondition: $t.__('common.actions.add_condition'),
    emptyTitle: $t.__('automation.AutomationWizardCondition.empty_title'),
    emptySubtitle: $t.__('automation.AutomationWizardCondition.empty_subtitle'),
    andLabel: $t.__('common.labels.and'),
};

const isLoadingCustomFields = ref(false);
const groupedCustomFields = ref<GroupedCustomFields[]>([]);

// Assigns a stable unique key to each condition object for v-for :key tracking,
// without adding a technical property to the data model.
const conditionKeyMap = new WeakMap<Condition, number>();
let nextConditionKey = 0;

const resolvePropertyCfObject = (group: string): string =>
    PROPERTY_GROUP_TO_CF_OBJECT[group] || group;

const allGroupedFields = computed((): FieldGroup[] => {
    const groupMap = new Map<
        string,
        { label: string; properties: ConditionField[]; customFields: ConditionField[] }
    >();

    for (const cfGroup of groupedCustomFields.value) {
        groupMap.set(cfGroup.objectType, {
            label: cfGroup.label,
            properties: [],
            customFields: [...cfGroup.fields],
        });
    }

    for (const prop of props.propertyDefinitions) {
        const cfObjectType = resolvePropertyCfObject(prop.group);
        if (!groupMap.has(cfObjectType)) {
            const label = OBJECT_TYPE_LABELS[cfObjectType]
                ? $t.__(OBJECT_TYPE_LABELS[cfObjectType])
                : cfObjectType;
            groupMap.set(cfObjectType, { label, properties: [], customFields: [] });
        }
        groupMap.get(cfObjectType)!.properties.push({
            id: prop.id,
            name: prop.label,
            type: prop.dataType,
            group: prop.group,
            isProperty: true,
        });
    }

    const sortByName = (fieldA: ConditionField, fieldB: ConditionField): number =>
        fieldA.name.localeCompare(fieldB.name);

    return Array.from(groupMap.values())
        .map((group) => ({
            label: group.label,
            fields: [...group.properties.sort(sortByName), ...group.customFields.sort(sortByName)],
        }))
        .filter((group) => group.fields.length > 0);
});

const allAvailableFields = computed((): ConditionField[] =>
    allGroupedFields.value.flatMap((group) => group.fields),
);

const rankedConditions = computed((): { condition: Condition; originalIndex: number }[] =>
    conditions.value
        .map((condition, originalIndex) => ({ condition, originalIndex }))
        .filter(({ condition }) => !!condition.rank),
);

const getConditionKey = (condition: Condition): number => {
    if (!conditionKeyMap.has(condition)) {
        conditionKeyMap.set(condition, ++nextConditionKey);
    }
    return conditionKeyMap.get(condition)!;
};

const addCondition = (): void => {
    const nextRank = rankedConditions.value.length + 1;
    conditions.value = [
        ...conditions.value,
        { scope: null, id: null, dataType: null, operator: null, value: null, rank: nextRank },
    ];
};

const removeCondition = (index: number): void => {
    const condition = conditions.value[index];
    if (condition.scope === ConditionScope.OBJECT_PROPERTIES) {
        const updated = [...conditions.value];
        updated[index] = {
            id: condition.id,
            label: condition.label,
            scope: condition.scope,
            dataType: condition.dataType,
            group: condition.group,
            operator: null,
            value: null,
        };
        conditions.value = updated;
    } else {
        conditions.value = conditions.value.filter((_condition, idx) => idx !== index);
    }
};

const updateCondition = (index: number, value: Condition): void => {
    Object.assign(conditions.value[index], value);
    conditions.value = [...conditions.value];
};

const loadCustomFields = async (): Promise<void> => {
    isLoadingCustomFields.value = true;
    try {
        const results: CustomField[][] = await Promise.all(
            props.objectTypes.map((type) => getAllCustomFields(type)),
        );
        groupedCustomFields.value = props.objectTypes
            .map((type, index) => ({
                objectType: type,
                label: $t.__(OBJECT_TYPE_LABELS[type] || type),
                fields: results[index].filter((field: CustomField) =>
                    SUPPORTED_CF_TYPES.includes(field.type),
                ),
            }))
            .filter((group) => group.fields.length > 0);
    } catch {
        groupedCustomFields.value = [];
        $pushToast({
            type: 'error',
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoadingCustomFields.value = false;
    }
};

const removeUnavailableConditions = (): void => {
    const availableFieldIds = new Set(allAvailableFields.value.map((field) => field.id));
    conditions.value = conditions.value.filter(
        (condition) => !condition.id || availableFieldIds.has(condition.id),
    );
};
__expose({ removeUnavailableConditions });

onMounted(() => {
    loadCustomFields();
});

return (_ctx: any,_cache: any) => {
  const _component_CompassSkeleton = _resolveComponent("CompassSkeleton")!
  const _component_CompassColoredIcon = _resolveComponent("CompassColoredIcon")!
  const _component_CompassButton = _resolveComponent("CompassButton")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoadingCustomFields.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_CompassSkeleton, {
                variant: _ctx.CompassEnums.SkeletonVariantType.SQUARE,
                size: _ctx.CompassEnums.SkeletonSize.MEDIUM
              }, null, 8 /* PROPS */, ["variant", "size"]),
              _createVNode(_component_CompassSkeleton, {
                size: _ctx.CompassEnums.SkeletonSize.MEDIUM
              }, null, 8 /* PROPS */, ["size"])
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_CompassSkeleton, {
                size: _ctx.CompassEnums.SkeletonSize.MEDIUM
              }, null, 8 /* PROPS */, ["size"]),
              _createVNode(_component_CompassSkeleton, {
                size: _ctx.CompassEnums.SkeletonSize.LARGE
              }, null, 8 /* PROPS */, ["size"])
            ])
          ])
        ]))
      : (rankedConditions.value.length > 0)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rankedConditions.value, (rankedCondition, index) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: getConditionKey(rankedCondition.condition)
              }, [
                (index > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(translations.andLabel), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true),
                _createVNode(AutomationWizardConditionRow, {
                  modelValue: rankedCondition.condition,
                  groupedFields: allGroupedFields.value,
                  "onUpdate:modelValue": 
                        (val) => updateCondition(rankedCondition.originalIndex, val)
                    ,
                  onRemove: ($event: any) => (removeCondition(rankedCondition.originalIndex))
                }, null, 8 /* PROPS */, ["modelValue", "groupedFields", "onUpdate:modelValue", "onRemove"])
              ], 64 /* STABLE_FRAGMENT */))
            }), 128 /* KEYED_FRAGMENT */)),
            _createCommentVNode(" TODO: uncomment when multiple conditions are supported\n            <CompassButton\n                :type=\"CompassEnums.ButtonType.SECONDARY\"\n                leftIcon=\"plus\"\n                :label=\"translations.addCondition\"\n                :disabled=\"allAvailableFields.length === 0\"\n                @click=\"addCondition\"\n            />\n            ")
          ], 64 /* STABLE_FRAGMENT */))
        : (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_component_CompassColoredIcon, {
                icon: "filter",
                color: _ctx.CompassEnums.ColoredIconColorType.PURPLE,
                size: _ctx.CompassEnums.ColoredIconSize.LARGE
              }, null, 8 /* PROPS */, ["color", "size"]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("p", _hoisted_10, _toDisplayString(translations.emptyTitle), 1 /* TEXT */),
                _createElementVNode("p", _hoisted_11, _toDisplayString(translations.emptySubtitle), 1 /* TEXT */)
              ]),
              _createVNode(_component_CompassButton, {
                type: _ctx.CompassEnums.ButtonType.SECONDARY,
                leftIcon: "plus",
                label: translations.addCondition,
                disabled: allAvailableFields.value.length === 0,
                onClick: addCondition
              }, null, 8 /* PROPS */, ["type", "label", "disabled"])
            ])
          ]))
  ]))
}
}

})